//ICS-208HM
import React from 'react';
import dayjs from 'dayjs';
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';


import { FormExportButton } from 'components/IAP/FormExportButton';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import FormVersioningBanner from 'components/IAP/femaCards/FormVersioningBanner';
import { useRecordFormVersionSave } from 'components/IAP/hooks/icsFormVersionHooks';
import {
  useIncident,
  useIncidentPermissions,
} from 'components/IAP/hooks/incidentHooks';
import {
  Material,
  useGetHazmats,
  useGetIncidentInfo,
  usePostHazmats,
  useUpdateIncidentInfo,
} from 'components/IAP/hooks/useSafetyAnalysis';
import {
  HAZMAT_CLASSIFICATION,
  INCIDENT_NEEDS,
  POTENTIAL_HAZARDS,
  REQUIRED_PPE,
  type HAZMATClassification,
  type IncidentNeed,
  type PotentialHazard,
  type RequiredPPE,
} from 'components/IAP/initialResponsePage/SafetyAnalysis/types';
import { useICSContext } from 'components/IAP/planningView/ICSContext';
import { useUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';

import { getIncidentCardTitle } from '../../USCGPage';
import { Ics204CardSection } from '../AssignmentList/ics204BoxLabels';
import { BoxInfo, PrintedValue } from '../icsBoxPrimitives';
import { IcsCheckboxGrid, IcsYesNoRadio } from '../IcsCheckboxGrid';
import Ics208HmSignatureBox from '../Ics208HmSignatureBox';
import {
  ICS208HM_BOX_LABELS,
  ICS208HM_DATE_TIME_OF_REPORT_HINT,
  ICS208HM_INCIDENT_LOCATION_HINT,
  ICS208HM_INCIDENT_NAME_HINT,
  ICS208HM_MATERIAL_HINT,
  ICS208HM_MEDICAL_HINT,
  ICS208HM_MONITORING_INSTRUMENTS,
  ICS208HM_POTENTIAL_EFFECTS_HINT,
  ICS208HM_REQUIRED_PPE_HINT,
} from '../ics208hmBoxLabels';
import SaveableBox from '../SaveableBox';
import SaveableField from '../SaveableField';
import MaterialList from './MaterialList';

const ICS_208HM_EXPORT_FORMNAME = 'ICS_208HM_CG';

/**
 * The whole card's content in one shape.
 *
 * Kept as one state even though it is stored in two places, because that split
 * is a storage detail: `potentialHazards`, `requiredPPE` and `notesOnPPE` live
 * on the incident information record and everything else on the hazmat
 * assessment. `saveBox` writes both, so a box never has to know which it is.
 *
 * Exported because `mapToHazmats` and `mapToIncidentInfo` are typed against it.
 */
export type HazardousMaterialsFormState = {
  hazmatClassification: HAZMATClassification[];
  material: Material[];
  potentialHazards: PotentialHazard[];
  incidentNeeds: IncidentNeed[];
  additionalProductInfo: string;
  requiredPPE: RequiredPPE[];
  notesOnPPE: string;
  lelInstruments: string;
  o2Instruments: string;
  toxicityInstruments: string;
  radiologicalInstruments: string;
  hazardMonitoringComments: string;
  standardOperatingProcedures: string;
  decontaminationProcedures: string;
  medicalMonitoringRequired: boolean;
  emergencyProcedures: string;
  medicalTreatmentAndTransportInPlace: boolean;
  otherClassification: null; // deprecated
  airMonitoringRequired: null; // deprecated
};

/** Which instrument blank of box 10 a field name belongs to. */
type InstrumentKey = (typeof ICS208HM_MONITORING_INSTRUMENTS)[number]['key'];

const yesNo = (value: boolean): string => (value ? 'yes' : 'no');

/**
 * Site Safety and Health Plan for hazardous materials, the ICS 208HM-CG.
 *
 * Page 1 is boxes 1 to 9 and page 2 is boxes 10 to 15, with the same Prepared
 * by printed at the foot of each.
 */
const HazardousMaterials: React.FC = () => {
  const { data: hazmatData } = useGetHazmats();
  const { data: incidentInfoData } = useGetIncidentInfo();
  const { data: incidentPermissions } = useIncidentPermissions();
  const canEditCards = incidentPermissions?.['incident:editCards'] ?? false;

  const { exportAsForm, isExporting } = useIcsFormPdfPreview();
  const { effectivePeriodNumber, isFuture, readOnlyForms } = useICSContext();
  const { isMapVisible } = useUscgMapContext();
  const readOnly = !canEditCards || readOnlyForms;

  const incident = useIncident();

  const formState: HazardousMaterialsFormState = React.useMemo(
    () => ({
      hazmatClassification: hazmatData?.classifications ?? [],
      material: hazmatData?.material ?? [],
      potentialHazards: incidentInfoData?.potentialHazards ?? [],
      incidentNeeds: hazmatData?.incidentNeeds ?? [],
      additionalProductInfo: hazmatData?.additionalProductInfo ?? '',
      requiredPPE: incidentInfoData?.requiredPpe ?? [],
      notesOnPPE: incidentInfoData?.notesOnPpe ?? '',
      lelInstruments: hazmatData?.lelInstruments ?? '',
      o2Instruments: hazmatData?.o2Instruments ?? '',
      toxicityInstruments: hazmatData?.toxicityInstruments ?? '',
      radiologicalInstruments: hazmatData?.radiologicalInstruments ?? '',
      hazardMonitoringComments: hazmatData?.hazardMonitoringComments ?? '',
      standardOperatingProcedures: hazmatData?.sops ?? '',
      decontaminationProcedures: hazmatData?.decontaminationProcedure ?? '',
      medicalMonitoringRequired: hazmatData?.medicalMonitoring ?? false,
      medicalTreatmentAndTransportInPlace:
        hazmatData?.medicalTreatment ?? false,
      emergencyProcedures: hazmatData?.emergencyProcedures ?? '',
      otherClassification: null,
      airMonitoringRequired: null,
    }),
    [hazmatData, incidentInfoData]
  );

  const postHazmats = usePostHazmats(formState);
  const updateIncidentInfo = useUpdateIncidentInfo(formState);

  const recordVersionSave = useRecordFormVersionSave(
    'ICS_208HM',
    effectivePeriodNumber
  );

  /**
   * One box's edit, written to both records.
   *
   * Both mutations take the whole card's state as an override, so a box can
   * hand over everything with its own field replaced and neither record loses a
   * value the box did not show.
   */
  const saveBox = React.useCallback(
    async (patch: Partial<HazardousMaterialsFormState>) => {
      const next = { ...formState, ...patch };
      await postHazmats.mutateAsync(next);
      await updateIncidentInfo.mutateAsync(next);
      if (!readOnlyForms) recordVersionSave.mutate();
    },
    [
      formState,
      postHazmats,
      updateIncidentInfo,
      readOnlyForms,
      recordVersionSave,
    ]
  );

  const handleExport = (format: 'pdf' | 'docx' = 'pdf') => {
    void exportAsForm({
      formname: ICS_208HM_EXPORT_FORMNAME,
      data: {},
      mapSketch: null,
      format,
    });
  };

  const exportActions = (
    <FormExportButton
      formname={ICS_208HM_EXPORT_FORMNAME}
      onExport={handleExport}
      label="Preview ICS-208HM"
      disabled={isExporting}
    />
  );

  const dateTimeOfReport = incidentInfoData?.timestamp
    ? dayjs(incidentInfoData.timestamp).format('MM/DD/YYYY HH:mm')
    : '';

  const body = (
    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3">
      <FormVersioningBanner baseId="ICS_208HM" />

      <div className="tw:grid tw:min-w-0 tw:gap-3 tw:md:grid-cols-3">
        <Ics204CardSection
          boxLabel={ICS208HM_BOX_LABELS.incidentName}
          testId="ics208hm-section-incident-name"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label="About the incident name">
              {ICS208HM_INCIDENT_NAME_HINT}
            </BoxInfo>
          }
        >
          <PrintedValue
            value={incident.data?.name ?? ''}
            testId="ics208hm-field-incident-name"
          />
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel={ICS208HM_BOX_LABELS.incidentLocation}
          testId="ics208hm-section-incident-location"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label="About the incident location">
              {ICS208HM_INCIDENT_LOCATION_HINT}
            </BoxInfo>
          }
        >
          <PrintedValue
            value={
              incidentInfoData?.incidentLocation || incident.data?.address || ''
            }
            testId="ics208hm-field-incident-location"
          />
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel={ICS208HM_BOX_LABELS.dateTimeOfReport}
          testId="ics208hm-section-date-time-of-report"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label="About the report timestamp">
              {ICS208HM_DATE_TIME_OF_REPORT_HINT}
            </BoxInfo>
          }
        >
          <PrintedValue
            value={dateTimeOfReport}
            testId="ics208hm-field-date-time-of-report"
          />
        </Ics204CardSection>
      </div>

      <Ics204CardSection
        boxLabel={ICS208HM_BOX_LABELS.hazmatClassification}
        testId="ics208hm-section-classification"
        className="tw:mt-0"
      >
        <SaveableBox<HAZMATClassification[]>
          id="ics208hm-classification"
          label={ICS208HM_BOX_LABELS.hazmatClassification}
          disabled={readOnly}
          value={formState.hazmatClassification}
          onSave={(next) => void saveBox({ hazmatClassification: next })}
        >
          {(draft, setDraft) => (
            <IcsCheckboxGrid
              label={ICS208HM_BOX_LABELS.hazmatClassification}
              hideLabel
              testId="ics208hm-classification-grid"
              columns={3}
              options={HAZMAT_CLASSIFICATION}
              value={draft}
              disabled={readOnly}
              onChange={(next) => setDraft(next as HAZMATClassification[])}
            />
          )}
        </SaveableBox>
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS208HM_BOX_LABELS.material}
        testId="ics208hm-section-material"
        className="tw:mt-0"
        headerAction={
          <BoxInfo label="About the material table">
            {ICS208HM_MATERIAL_HINT}
          </BoxInfo>
        }
      >
        <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-4">
          {/* The table commits on its own, because a dialog already gives each
              row its own Save. */}
          <MaterialList
            material={formState.material}
            updateMaterials={(material) => void saveBox({ material })}
            readOnly={readOnly}
          />
          <SaveableField
            id="ics208hm-additional-product-info"
            label={ICS208HM_BOX_LABELS.additionalProductInfo}
            multiline
            rows={4}
            placeholder="Enter a description..."
            disabled={readOnly}
            value={formState.additionalProductInfo}
            onSave={(value) => void saveBox({ additionalProductInfo: value })}
          />
        </div>
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS208HM_BOX_LABELS.potentialPhysicalEffects}
        testId="ics208hm-section-potential-effects"
        className="tw:mt-0"
        headerAction={
          <BoxInfo label="About the potential physical effects">
            {ICS208HM_POTENTIAL_EFFECTS_HINT}
          </BoxInfo>
        }
      >
        <SaveableBox<PotentialHazard[]>
          id="ics208hm-potential-effects"
          label={ICS208HM_BOX_LABELS.potentialPhysicalEffects}
          disabled={readOnly}
          value={formState.potentialHazards}
          onSave={(next) => void saveBox({ potentialHazards: next })}
        >
          {(draft, setDraft) => (
            <IcsCheckboxGrid
              label={ICS208HM_BOX_LABELS.potentialPhysicalEffects}
              hideLabel
              testId="ics208hm-potential-effects-grid"
              // Each option is a full sentence, so they stack rather than tile.
              columns={2}
              options={POTENTIAL_HAZARDS}
              value={draft}
              disabled={readOnly}
              onChange={(next) => setDraft(next as PotentialHazard[])}
            />
          )}
        </SaveableBox>
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS208HM_BOX_LABELS.incidentNeeds}
        testId="ics208hm-section-incident-needs"
        className="tw:mt-0"
      >
        <SaveableBox<IncidentNeed[]>
          id="ics208hm-incident-needs"
          label={ICS208HM_BOX_LABELS.incidentNeeds}
          disabled={readOnly}
          value={formState.incidentNeeds}
          onSave={(next) => void saveBox({ incidentNeeds: next })}
        >
          {(draft, setDraft) => (
            <IcsCheckboxGrid
              label={ICS208HM_BOX_LABELS.incidentNeeds}
              hideLabel
              testId="ics208hm-incident-needs-grid"
              columns={3}
              options={INCIDENT_NEEDS}
              value={draft}
              disabled={readOnly}
              onChange={(next) => setDraft(next as IncidentNeed[])}
            />
          )}
        </SaveableBox>
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS208HM_BOX_LABELS.requiredPpe}
        testId="ics208hm-section-required-ppe"
        className="tw:mt-0"
        headerAction={
          <BoxInfo label="About the required PPE">
            {ICS208HM_REQUIRED_PPE_HINT}
          </BoxInfo>
        }
      >
        <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-4">
          <SaveableBox<RequiredPPE[]>
            id="ics208hm-required-ppe"
            label={ICS208HM_BOX_LABELS.requiredPpe}
            disabled={readOnly}
            value={formState.requiredPPE}
            onSave={(next) => void saveBox({ requiredPPE: next })}
          >
            {(draft, setDraft) => (
              <IcsCheckboxGrid
                label={ICS208HM_BOX_LABELS.requiredPpe}
                hideLabel
                testId="ics208hm-required-ppe-grid"
                columns={4}
                options={REQUIRED_PPE}
                value={draft}
                disabled={readOnly}
                onChange={(next) => setDraft(next as RequiredPPE[])}
              />
            )}
          </SaveableBox>
          <SaveableField
            id="ics208hm-notes-on-ppe"
            label="Comments on PPE"
            multiline
            rows={3}
            placeholder="Enter comments..."
            disabled={readOnly}
            value={formState.notesOnPPE}
            onSave={(value) => void saveBox({ notesOnPPE: value })}
          />
        </div>
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS208HM_BOX_LABELS.hazardMonitoring}
        testId="ics208hm-section-hazard-monitoring"
        className="tw:mt-0"
      >
        <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-4">
          <div className="tw:grid tw:min-w-0 tw:gap-3 tw:md:grid-cols-2 tw:xl:grid-cols-4">
            {ICS208HM_MONITORING_INSTRUMENTS.map((instrument) => (
              <SaveableField
                key={instrument.key}
                id={`ics208hm-${instrument.key}`}
                label={instrument.label}
                placeholder="Enter instrument notes..."
                disabled={readOnly}
                value={formState[instrument.key as InstrumentKey]}
                onSave={(value) => void saveBox({ [instrument.key]: value })}
              />
            ))}
          </div>
          <SaveableField
            id="ics208hm-hazard-monitoring-comments"
            label="Comments"
            multiline
            rows={3}
            placeholder="Enter comments..."
            disabled={readOnly}
            value={formState.hazardMonitoringComments}
            onSave={(value) =>
              void saveBox({ hazardMonitoringComments: value })
            }
          />
        </div>
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS208HM_BOX_LABELS.sopAndSafeWorkPractices}
        testId="ics208hm-section-sop"
        className="tw:mt-0"
      >
        <SaveableField
          id="ics208hm-sop"
          label={ICS208HM_BOX_LABELS.sopAndSafeWorkPractices}
          hideLabel
          multiline
          rows={4}
          placeholder="Enter comments..."
          disabled={readOnly}
          value={formState.standardOperatingProcedures}
          onSave={(value) =>
            void saveBox({ standardOperatingProcedures: value })
          }
        />
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS208HM_BOX_LABELS.decontaminationProcedures}
        testId="ics208hm-section-decontamination"
        className="tw:mt-0"
      >
        <SaveableField
          id="ics208hm-decontamination"
          label={ICS208HM_BOX_LABELS.decontaminationProcedures}
          hideLabel
          multiline
          rows={4}
          placeholder="Enter procedures..."
          disabled={readOnly}
          value={formState.decontaminationProcedures}
          onSave={(value) => void saveBox({ decontaminationProcedures: value })}
        />
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS208HM_BOX_LABELS.medical}
        testId="ics208hm-section-medical"
        className="tw:mt-0"
        headerAction={
          <BoxInfo label="About the medical questions">
            {ICS208HM_MEDICAL_HINT}
          </BoxInfo>
        }
      >
        <SaveableBox<{ monitoring: string; treatment: string }>
          id="ics208hm-medical"
          label={ICS208HM_BOX_LABELS.medical}
          disabled={readOnly}
          value={{
            monitoring: yesNo(formState.medicalMonitoringRequired),
            treatment: yesNo(formState.medicalTreatmentAndTransportInPlace),
          }}
          onSave={(draft) =>
            void saveBox({
              medicalMonitoringRequired: draft.monitoring === 'yes',
              medicalTreatmentAndTransportInPlace: draft.treatment === 'yes',
            })
          }
        >
          {(draft, setDraft) => (
            <div className="tw:grid tw:min-w-0 tw:gap-3 tw:md:grid-cols-2">
              <IcsYesNoRadio
                label="A. Medical Monitoring required?"
                testId="ics208hm-medical-monitoring"
                value={draft.monitoring}
                disabled={readOnly}
                onChange={(next) => setDraft({ ...draft, monitoring: next })}
              />
              <IcsYesNoRadio
                label="B. Medical Treatment and Transport in place?"
                testId="ics208hm-medical-treatment"
                value={draft.treatment}
                disabled={readOnly}
                onChange={(next) => setDraft({ ...draft, treatment: next })}
              />
            </div>
          )}
        </SaveableBox>
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS208HM_BOX_LABELS.emergencyProcedures}
        testId="ics208hm-section-emergency-procedures"
        className="tw:mt-0"
      >
        <SaveableField
          id="ics208hm-emergency-procedures"
          label={ICS208HM_BOX_LABELS.emergencyProcedures}
          hideLabel
          multiline
          rows={4}
          placeholder="Enter procedures..."
          disabled={readOnly}
          value={formState.emergencyProcedures}
          onSave={(value) => void saveBox({ emergencyProcedures: value })}
        />
      </Ics204CardSection>

      <Ics208HmSignatureBox periodNumber={effectivePeriodNumber} />
    </div>
  );

  // Beside the map on a future period there is no room for card chrome, so the
  // boxes stand on the panel itself, as the 202 does.
  if (isMapVisible && isFuture) {
    return <div className="dark content tw:text-foreground mt-2">{body}</div>;
  }

  return (
    <Card
      data-testid="uscg-208hm-card"
      data-tour="ics208hm-card"
      data-form-family="uscg"
      className="dark tw:bg-background tw:text-foreground"
    >
      <CardHeader>
        <CardTitle>{getIncidentCardTitle('hazardousMaterials')}</CardTitle>
        <CardAction>{exportActions}</CardAction>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
};

export default HazardousMaterials;
